import packageJson from "package.json"

const { version } = packageJson

type UpdateCallback = () => void
type UpdateObject = Record<string, UpdateCallback>

interface Update {
  version: string
  updates: UpdateObject
}
/**
 Class to manage and execute scripts associated with version updates.

 Register any updates using the `registerUpdate` method.
 Finally, execute using the `upgrade` method. The updates will occur synchronously in the order they are registered.
 
 A stringified `versioning` object is stored in localStorage to track which updates have already been applied.
 
 Example usage: 
 ```
 VersionManager.registerUpdate('1.0.0', { clearGridCache })
 VersionManager.upgrade()
 ```
 */
export class VersionManager {
  public static readonly version: string = version
  private static updates: Update[] = []

  private constructor() {}

  public static registerUpdate(
    targetVersion: string,
    updates: UpdateObject
  ): void {
    this.updates.push({ version: targetVersion, updates })
  }

  private static checkIfShouldExecute(
    fnName: string,
    targetVersion: string
  ): boolean {
    if (this.version !== targetVersion) return false

    const versioning = JSON.parse(localStorage.getItem("versioning") || "{}")
    if (versioning[fnName]?.applied) return false

    localStorage.setItem(
      "versioning",
      JSON.stringify({
        ...versioning,
        [fnName]: { applied: true },
      })
    )

    return true
  }

  private static applyUpdate(
    targetVersion: string,
    fnName: string,
    fn: UpdateCallback
  ): void {
    if (this.checkIfShouldExecute(fnName, targetVersion)) {
      try {
        fn()
        console.log(`Applied ${fnName} for version ${targetVersion}`)
      } catch (error) {
        console.error(
          `Error executing function ${fnName} for version ${targetVersion}:`,
          error
        )
      }
    }
  }

  public static upgrade(): void {
    for (const { version, updates } of this.updates) {
      for (const [name, fn] of Object.entries(updates)) {
        this.applyUpdate(version, name, fn)
      }
    }
  }
}
